import type { ReactNode } from 'react';
import { Avatar } from './Avatar.js';
import { cn } from './cn.js';
import { COLOR_CLASSES } from './ColorPicker.js';
import { Icon, type IconName } from './Icon.js';
import { PersonMark } from './PersonMark.js';
import { progressPercent, type KanbanColor } from './kanbanState.js';

/**
 * O miolo de um cartão de quadro, na anatomia da casa — o que `renderCard` do
 * `Kanban` devolve quando o módulo não tem motivo para desenhar outra coisa.
 *
 * De cima para baixo: etiquetas, título, progresso, rodapé (prazo à esquerda,
 * responsável à direita). **Toda região é opcional e some inteira** quando o
 * módulo não a tem: cartão sem subtarefa não desenha "0/0 subtarefas" nem uma
 * barra vazia, que diriam que existe uma lista e ninguém começou.
 *
 * Nada aqui é botão nem link — a casca do quadro já é o alvo do clique e do
 * arrasto. Ver o docblock do `Kanban`.
 */
export function KanbanCardBody({
  tags,
  title,
  progress,
  detail,
  detailIcon = 'relogio',
  assignee,
  owner,
  alerts,
}: {
  /** A linha do alto — `KanbanTag`s: prioridade, categoria. */
  tags?: ReactNode;
  title: ReactNode;
  /**
   * Subtarefas feitas de quantas. `label` troca a palavra — "3/4 documentos".
   */
  progress?: { done: number; total: number; label?: string };
  /** O dado do rodapé à esquerda — o prazo, na maioria dos quadros. */
  detail?: ReactNode;
  detailIcon?: IconName;
  /** Nome de quem está com o cartão; `null` desenha a pastilha vazia. */
  assignee?: string | null;
  /**
   * O responsável com nome escrito (`PersonMark`), numa linha própria abaixo
   * do título — no lugar da pastilha de iniciais, quando o quadro quer que o
   * dono se leia de longe. `name` nulo diz `emptyLabel`.
   */
  owner?: { name: string | null; emptyLabel?: string };
  /**
   * Os prazos do cartão — o SLA da fase e o vencimento (07/10/2026), em
   * `KanbanTag`s com ícone. Ficam no pé, separados das etiquetas do alto:
   * etiqueta é o que alguém marcou, prazo é o que o relógio diz.
   */
  alerts?: ReactNode;
}) {
  const percent = progress ? progressPercent(progress.done, progress.total) : 0;
  const hasFooter = detail !== undefined || assignee !== undefined;

  return (
    <div className="flex flex-col gap-2.5">
      {tags && <div className="flex flex-wrap items-center gap-1.5">{tags}</div>}

      <p className="text-sm font-medium break-words">{title}</p>

      {owner && <PersonMark name={owner.name} emptyLabel={owner.emptyLabel} />}

      {progress && progress.total > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-2 text-xs text-texto-suave">
            <span className="tabular-nums">
              {progress.done}/{progress.total} {progress.label ?? 'subtarefas'}
            </span>
            <span className="tabular-nums">{percent}%</span>
          </div>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-label={`${progress.label ?? 'Subtarefas'} concluídas`}
            className="h-1 overflow-hidden rounded-pilula bg-superficie-suave"
          >
            <div
              className="h-full rounded-pilula bg-progresso"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      )}

      {hasFooter && (
        <div className="flex items-center justify-between gap-2">
          {detail !== undefined ? (
            <span className="flex min-w-0 items-center gap-1.5 text-xs text-texto-suave">
              <Icon nome={detailIcon} size={16} className="shrink-0" />
              <span className="truncate">{detail}</span>
            </span>
          ) : (
            <span />
          )}
          {assignee !== undefined && <Avatar nome={assignee} size="sm" />}
        </div>
      )}

      {alerts && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-dashed border-borda pt-2">
          {alerts}
        </div>
      )}
    </div>
  );
}

/**
 * Uma etiqueta do cartão — prioridade, categoria.
 *
 * Não é `Badge`: o `Badge` da casa é SITUAÇÃO (cor, ícone e texto, os três), e
 * uma categoria não é situação. Por isso ela é neutra por padrão. `tone` existe
 * para o módulo que já tem uma situação de verdade para mostrar aqui — e aí o
 * texto continua dizendo qual é, a cor só reforça.
 *
 * `color` é a etiqueta escolhida por quem usa (07/10/2026): o fundo suave e o
 * ponto da paleta de fase, com o texto na cor do texto — a cor ajuda a achar,
 * e o nome é quem diz. Com `color`, `tone` não vale.
 */
export function KanbanTag({
  tone = 'neutro',
  color,
  icon,
  title,
  children,
}: {
  tone?: 'neutro' | 'alerta' | 'erro' | 'sucesso' | 'progresso';
  color?: KanbanColor;
  /** Um ícone antes do texto — o calendário do vencimento, o relógio do SLA. */
  icon?: IconName;
  /** O texto longo, no `title` — "Urgência: Alta". */
  title?: string;
  children: ReactNode;
}) {
  if (color) {
    const classes = COLOR_CLASSES[color];
    return (
      <span
        title={title}
        className={cn(
          'inline-flex max-w-full items-center gap-1.5 rounded-pilula px-2 py-0.5 text-xs font-medium text-texto',
          classes.column,
        )}
      >
        <span aria-hidden className={cn('size-2 shrink-0 rounded-pilula', classes.dot)} />
        <span className="truncate">{children}</span>
      </span>
    );
  }
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-pilula px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        TAG_TONES[tone],
      )}
    >
      {icon && <Icon nome={icon} size={16} className={cn('size-3.5', TAG_ICON_TONES[tone])} />}
      {children}
    </span>
  );
}

/* No selo de alerta e de erro, o ícone leva a cor da situação e o texto fica
   na cor do texto — a régua de contraste mede o texto, não o ícone. */
const TAG_ICON_TONES = {
  neutro: '',
  alerta: 'text-alerta',
  erro: 'text-erro',
  sucesso: 'text-sucesso',
  progresso: '',
} as const;

const TAG_TONES = {
  neutro: 'bg-superficie-suave text-texto-suave',
  alerta: 'bg-alerta-suave text-texto',
  erro: 'bg-erro-suave text-texto',
  sucesso: 'bg-sucesso-suave text-texto',
  progresso: 'bg-progresso-suave text-progresso',
} as const;
